import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Edit, Trash2, Plus, RefreshCw, CheckCircle, XCircle, Eye, Calendar, TrendingUp } from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import buyingGuideService from '../../services/buyingGuideService';
import GuideFormModal from './components/GuideFormModal';
import Icon from '../../components/AppIcon';

const AdminBuyingGuideManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [guides, setGuides] = useState([]);
  const [filteredGuides, setFilteredGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedGuides, setSelectedGuides] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    totalViews: 0
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin-login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !authLoading) {
      loadGuides();
    }
  }, [user, authLoading]);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [searchQuery, selectedCategory, selectedStatus, guides]);

  const loadGuides = async () => {
    try {
      setLoading(true);
      const data = await buyingGuideService?.getAllGuides();
      setGuides(data || []);
      setFilteredGuides(data || []);
    } catch (error) {
      console.error('Error loading guides:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const total = guides?.length;
    const published = guides?.filter(g => g?.isPublished)?.length;
    const draft = total - published;
    const totalViews = guides?.reduce((sum, g) => sum + (g?.viewCount || 0), 0);

    setStats({ total, published, draft, totalViews });
  };

  const applyFilters = () => {
    let filtered = [...guides];

    if (searchQuery) {
      filtered = filtered?.filter(guide =>
        guide?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        guide?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        guide?.targetAudience?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(guide => guide?.category === selectedCategory);
    }

    if (selectedStatus !== 'all') {
      if (selectedStatus === 'published') {
        filtered = filtered?.filter(guide => guide?.isPublished);
      } else if (selectedStatus === 'draft') {
        filtered = filtered?.filter(guide => !guide?.isPublished);
      }
    }

    setFilteredGuides(filtered);
  };

  const handleSelectGuide = (guideId) => {
    setSelectedGuides(prev =>
      prev?.includes(guideId)
        ? prev?.filter(id => id !== guideId)
        : [...prev, guideId]
    );
  };

  const handleSelectAll = () => {
    if (selectedGuides?.length === filteredGuides?.length) {
      setSelectedGuides([]);
    } else {
      setSelectedGuides(filteredGuides?.map(g => g?.id));
    }
  };

  const handleCreateGuide = () => {
    navigate('/ai-buying-guide-generator');
  };

  const handleEditGuide = (guide) => {
    setEditingGuide(guide);
    setShowGuideModal(true);
  };

  const handleDeleteGuide = async (guideId) => {
    if (!window.confirm('Are you sure you want to delete this guide?')) {
      return;
    }

    try {
      await buyingGuideService?.deleteGuide(guideId);
      await loadGuides();
    } catch (error) {
      console.error('Error deleting guide:', error);
      alert('Failed to delete guide');
    }
  };

  const handleTogglePublish = async (guideId, currentStatus) => {
    try {
      await buyingGuideService?.togglePublishStatus(guideId, !currentStatus);
      await loadGuides();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      alert('Failed to update publish status');
    }
  };

  const handleBulkPublish = async () => {
    try {
      await Promise.all(
        selectedGuides?.map(id => buyingGuideService?.togglePublishStatus(id, true))
      );
      await loadGuides();
      setSelectedGuides([]);
    } catch (error) {
      console.error('Error bulk publishing:', error);
      alert('Failed to publish guides');
    }
  };

  const handleBulkUnpublish = async () => {
    try {
      await Promise.all(
        selectedGuides?.map(id => buyingGuideService?.togglePublishStatus(id, false))
      );
      await loadGuides();
      setSelectedGuides([]);
    } catch (error) {
      console.error('Error bulk unpublishing:', error);
      alert('Failed to unpublish guides');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedGuides?.length} guides?`)) {
      return;
    }

    try {
      await Promise.all(
        selectedGuides?.map(id => buyingGuideService?.deleteGuide(id))
      );
      await loadGuides();
      setSelectedGuides([]);
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert('Failed to delete guides');
    }
  };

  const getStatusBadge = (guide) => {
    if (guide?.isPublished) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
          <CheckCircle size={12} />
          Published
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
        <XCircle size={12} />
        Draft
      </span>
    );
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'home-size': 'Home Size',
      'floor-type': 'Floor Type',
      'lifestyle': 'Lifestyle',
      'budget': 'Budget',
      'seasonal': 'Seasonal',
      'technology': 'Technology'
    };
    return labels?.[category] || category;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-text-primary">Buying Guide Management</h1>
              <p className="text-text-secondary mt-1">Manage and organize your buying guide content</p>
            </div>
            <Button
              onClick={handleCreateGuide}
              iconName="Plus"
              iconPosition="left"
            >
              Generate New Guide
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary">Total Guides</p>
                  <p className="text-2xl font-bold text-text-primary">{stats?.total}</p>
                </div>
                <Icon name="FileText" size={24} className="text-blue-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary">Published</p>
                  <p className="text-2xl font-bold text-green-600">{stats?.published}</p>
                </div>
                <CheckCircle size={24} className="text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary">Drafts</p>
                  <p className="text-2xl font-bold text-gray-600">{stats?.draft}</p>
                </div>
                <XCircle size={24} className="text-gray-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary">Total Views</p>
                  <p className="text-2xl font-bold text-purple-600">{stats?.totalViews?.toLocaleString()}</p>
                </div>
                <TrendingUp size={24} className="text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-subtle border border-border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search guides by title, category, or audience..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                icon={<Search size={20} />}
              />
            </div>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="md:w-48"
            >
              <option value="all">All Categories</option>
              <option value="home-size">Home Size</option>
              <option value="floor-type">Floor Type</option>
              <option value="lifestyle">Lifestyle</option>
              <option value="budget">Budget</option>
              <option value="seasonal">Seasonal</option>
              <option value="technology">Technology</option>
            </Select>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e?.target?.value)}
              className="md:w-48"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </Select>
            <Button
              variant="outline"
              onClick={loadGuides}
              iconName="RefreshCw"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedGuides?.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-blue-900">
                {selectedGuides?.length} guide{selectedGuides?.length !== 1 ? 's' : ''} selected
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkPublish}
                  iconName="CheckCircle"
                  iconPosition="left"
                >
                  Publish
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkUnpublish}
                  iconName="XCircle"
                  iconPosition="left"
                >
                  Unpublish
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkDelete}
                  iconName="Trash2"
                  iconPosition="left"
                  className="text-red-600 hover:text-red-700"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Guides Table */}
        <div className="bg-white rounded-xl shadow-subtle border border-border overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="text-text-secondary mt-4">Loading guides...</p>
            </div>
          ) : filteredGuides?.length === 0 ? (
            <div className="p-12 text-center">
              <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-text-secondary">No buying guides found</p>
              <Button
                onClick={handleCreateGuide}
                className="mt-4"
                iconName="Plus"
                iconPosition="left"
              >
                Create First Guide
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/30 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <Checkbox
                        checked={selectedGuides?.length === filteredGuides?.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Guide Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Target Audience
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Views
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredGuides?.map((guide) => (
                    <tr key={guide?.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-6 py-4">
                        <Checkbox
                          checked={selectedGuides?.includes(guide?.id)}
                          onCheckedChange={() => handleSelectGuide(guide?.id)}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {guide?.generatedByAi && (
                            <Icon name="Sparkles" size={14} className="text-purple-600" />
                          )}
                          <div>
                            <p className="font-medium text-text-primary">{guide?.title}</p>
                            {guide?.featured && (
                              <span className="text-xs text-amber-600 font-medium">Featured</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-text-secondary">
                          {getCategoryLabel(guide?.category)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-text-secondary">{guide?.targetAudience || '-'}</span>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(guide)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-text-secondary">
                          <Eye size={14} />
                          {guide?.viewCount || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-text-secondary">
                          <Calendar size={14} />
                          {new Date(guide?.updatedAt)?.toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleTogglePublish(guide?.id, guide?.isPublished)}
                            className="p-2 hover:bg-muted/50 rounded-lg transition-colors"
                            title={guide?.isPublished ? 'Unpublish' : 'Publish'}
                          >
                            {guide?.isPublished ? (
                              <XCircle size={18} className="text-gray-600" />
                            ) : (
                              <CheckCircle size={18} className="text-green-600" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEditGuide(guide)}
                            className="p-2 hover:bg-muted/50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit size={18} className="text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteGuide(guide?.id)}
                            className="p-2 hover:bg-muted/50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} className="text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {showGuideModal && (
        <GuideFormModal
          guide={editingGuide}
          onClose={() => {
            setShowGuideModal(false);
            setEditingGuide(null);
          }}
          onSave={async () => {
            await loadGuides();
            setShowGuideModal(false);
            setEditingGuide(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminBuyingGuideManagement;